import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, Card, Modal, PageStatus } from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import { useState } from 'react';
import { match, P } from 'ts-pattern';
import api from '~/api';
import { DataList, type DataListEntry } from '~/components/DataList';
import { DataTable, DataTableStatus } from '~/components/dataTable';
import { useStoreId } from '~/providers/StoreProvider';
import { extensionHandleLabel } from '~/utils/extensionHandleLabel';
import { formatter } from 'corso-web-lib';

type StoreExtension = CommandAdminApi.ResponseBody<
  '/store/:storeId/extension',
  'get'
>[number];

// the appHandle is unique per row, and DataTable keys on `id`
type ExtensionRow = StoreExtension & { id: string };

type LocationRow = StoreExtension['locations'][number] & { id: string };

const MS_PER_DAY = 86_400_000;

// same buckets as the weekly Slack digest in runExtensionReport.ts
const IDLE_DAYS = 3;
const INACTIVE_DAYS = 7;

function HealthBadge({ lastSeen }: { lastSeen: string | null }) {
  const daysSinceSeen =
    lastSeen === null
      ? null
      : Math.floor((Date.now() - new Date(lastSeen).getTime()) / MS_PER_DAY);

  const { variant, label } = match(daysSinceSeen)
    .with(
      P.nullish,
      () => ({ variant: 'default', label: 'Never seen' }) as const,
    )
    .with(
      P.number.gte(INACTIVE_DAYS),
      () => ({ variant: 'danger', label: 'Inactive' }) as const,
    )
    .with(
      P.number.gte(IDLE_DAYS),
      () => ({ variant: 'warning', label: 'Idle' }) as const,
    )
    .otherwise(() => ({ variant: 'success', label: 'Active' }) as const);

  return <Badge variant={variant}>{label}</Badge>;
}

const extensionColumns: ColumnDef<ExtensionRow>[] = [
  {
    id: 'name',
    header: 'Extension',
    accessorFn: (row) => extensionHandleLabel[row.appHandle],
    enableSorting: true,
  },
  {
    id: 'health',
    header: 'Health',
    // never-seen sorts last under `desc`, so the live extensions lead
    accessorFn: (row) => row.lastSeen ?? '',
    enableSorting: true,
    sortingFn: 'text',
    cell: ({ row }) => <HealthBadge lastSeen={row.original.lastSeen} />,
  },
  {
    id: 'lastSeen',
    header: 'Last Seen',
    cell: ({ row }) =>
      row.original.lastSeen
        ? formatter.relativeTimeFromNow(new Date(row.original.lastSeen))
        : '—',
  },
  {
    id: 'locations',
    header: 'Locations',
    accessorFn: (row) => row.locations.length,
    meta: { align: 'right' },
    enableSorting: true,
  },
];

const locationColumns: ColumnDef<LocationRow>[] = [
  {
    accessorKey: 'location',
    header: 'Location',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="font-mono break-all">{row.original.location}</span>
    ),
  },
  {
    id: 'firstSeen',
    header: 'First Seen',
    accessorFn: (row) => row.firstSeen,
    enableSorting: true,
    sortingFn: 'text',
    cell: ({ row }) =>
      formatter.relativeTimeFromNow(new Date(row.original.firstSeen)),
  },
  {
    id: 'lastSeen',
    header: 'Last Seen',
    accessorFn: (row) => row.lastSeen,
    enableSorting: true,
    sortingFn: 'text',
    cell: ({ row }) =>
      formatter.relativeTimeFromNow(new Date(row.original.lastSeen)),
  },
];

function ExtensionDetailModal({
  extension,
  onClose,
}: {
  extension: ExtensionRow;
  onClose: () => void;
}) {
  const items = [
    { label: 'Handle', value: extension.appHandle, copyEnabled: true },
    {
      label: 'Health',
      value: <HealthBadge lastSeen={extension.lastSeen} />,
    },
    {
      label: 'First Seen',
      value: extension.firstSeen
        ? formatter.relativeTimeFromNow(new Date(extension.firstSeen))
        : '—',
    },
    {
      label: 'Last Seen',
      value: extension.lastSeen
        ? formatter.relativeTimeFromNow(new Date(extension.lastSeen))
        : '—',
    },
  ] satisfies DataListEntry[];

  const locations = extension.locations.map((location) => ({
    ...location,
    id: location.location,
  }));

  return (
    <Modal
      show
      size="xl"
      onClose={onClose}
      title={extensionHandleLabel[extension.appHandle]}
      description={
        extension.lastSeen
          ? 'This extension reports in every time a shopper loads a page it renders on, so recency follows storefront traffic as much as extension health.'
          : 'This extension has never reported in. It is either not installed or not enabled on this store.'
      }
    >
      <div className="flex flex-col gap-4">
        <DataList items={items} />

        <div className="flex flex-col gap-2">
          <Card.Heading as="h3">Locations</Card.Heading>
          <Card.Description>
            Where this extension renders on the storefront.
          </Card.Description>
          {locations.length > 0 ? (
            <DataTable
              data={locations}
              columns={locationColumns}
              defaultSort={[{ id: 'lastSeen', desc: true }]}
              pageSize={10}
            />
          ) : (
            <DataTableStatus
              icon="location"
              title="No locations"
              details="This extension has not rendered anywhere on the storefront yet."
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export function ExtensionHealthCard() {
  const storeId = useStoreId();
  const [selected, setSelected] = useState<ExtensionRow | null>(null);

  const query = useQuery({
    queryKey: ['store', storeId, 'extensions'],
    queryFn: () => api.store(storeId).listExtensions(),
  });

  return (
    <Card>
      <Card.Heading>Shopify Extensions</Card.Heading>

      {match(query)
        .with({ status: 'pending' }, () => (
          <PageStatus.Loading title="Loading extensions" />
        ))
        .with({ status: 'error' }, () => (
          <PageStatus.Error
            title="Failed to load extensions"
            error={query.error}
          />
        ))
        .with({ status: 'success', data: P.select() }, (extensions) => (
          <>
            <DataTable
              data={extensions.map((extension) => ({
                ...extension,
                id: extension.appHandle,
              }))}
              columns={extensionColumns}
              defaultSort={[{ id: 'health', desc: true }]}
              pageSize={false}
              onRowClick={setSelected}
            />
            {selected && (
              <ExtensionDetailModal
                extension={selected}
                onClose={() => setSelected(null)}
              />
            )}
          </>
        ))
        .exhaustive()}
    </Card>
  );
}
